import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { calculateNewsKeywordScore } from "../lib/analysis/newsSentiment.js";
import { enrichHistoryWithIndicators, getLatestIndicators } from "../lib/analysis/indicators.js";
import { calculateSignal } from "../lib/analysis/signalEngine.js";
import { runEmaCrossoverBacktest } from "../lib/analysis/backtest.js";
import { DISCLAIMER } from "../lib/utils/constants.js";
import { formatNumber, formatPercent, formatUsd, formatZurichDateTime } from "../lib/utils/formatters.js";
import { fallbackCalendar, fallbackGoldHistory, fallbackGoldPrice, fallbackMacro, fallbackNews } from "../../lib-cloudflare/fallbackData.js";
import AlertsPanel from "./AlertsPanel.jsx";
import ApiStatusBadge from "./ApiStatusBadge.jsx";
import BacktestPanel from "./BacktestPanel.jsx";
import EconomicCalendarPanel from "./EconomicCalendarPanel.jsx";
import MacroPanel from "./MacroPanel.jsx";
import MainChart from "./MainChart.jsx";
import NewsPanel from "./NewsPanel.jsx";
import PortfolioTracker from "./PortfolioTracker.jsx";
import RiskCalculator from "./RiskCalculator.jsx";
import SignalCard from "./SignalCard.jsx";
import TechnicalPanel from "./TechnicalPanel.jsx";

async function fetchJson(path, options) {
  const response = await fetch(path, {
    headers: { "content-type": "application/json" },
    ...options,
  });
  if (!response.ok) throw new Error(`${path} failed`);
  return response.json();
}

function buildClientFallback() {
  const price = fallbackGoldPrice("Локальный frontend fallback активен");
  const history = fallbackGoldHistory("90d");
  const macro = fallbackMacro(["api"]);
  const news = fallbackNews("Локальный frontend fallback активен");
  const calendar = fallbackCalendar("Локальный frontend fallback активен");
  const newsAnalysis = calculateNewsKeywordScore(news, macro);
  const signal = calculateSignal({
    price,
    history,
    macro,
    newsAnalysis,
    calendar,
    apiStatus: { fallbackActive: true, geminiFallback: true, apiError: true, missingFields: ["api"] },
  });
  return {
    price,
    history,
    macro,
    news,
    calendar,
    newsAnalysis,
    signal,
    status: { cacheBinding: false, dbBinding: false, secrets: {} },
    loading: false,
    error: "Frontend fallback активен",
  };
}

export default function GoldDashboard() {
  const [state, setState] = useState(() => ({ ...buildClientFallback(), loading: true, error: null }));

  useEffect(() => {
    let active = true;

    async function loadDashboard() {
      try {
        const [price, history, macro, news, calendar, signal, status] = await Promise.all([
          fetchJson("/api/gold/price"),
          fetchJson("/api/gold/history?interval=1d&range=90d"),
          fetchJson("/api/macro"),
          fetchJson("/api/news"),
          fetchJson("/api/economic-calendar"),
          fetchJson("/api/signal"),
          fetchJson("/api/status").catch(() => ({ cacheBinding: false, dbBinding: false, secrets: {} })),
        ]);

        const newsAnalysis = await fetchJson("/api/ai/news-analysis", {
          method: "POST",
          body: JSON.stringify({ news, macro }),
        }).catch(() => calculateNewsKeywordScore(news, macro));

        if (!active) return;
        setState({
          price,
          history: Array.isArray(history) ? history : fallbackGoldHistory("90d"),
          macro,
          news: Array.isArray(news) ? news : [],
          calendar: Array.isArray(calendar) ? calendar : [],
          newsAnalysis,
          signal,
          status,
          loading: false,
          error: null,
        });
      } catch {
        if (!active) return;
        setState(buildClientFallback());
      }
    }

    loadDashboard();
    const timer = window.setInterval(loadDashboard, 180000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  const enrichedHistory = useMemo(() => enrichHistoryWithIndicators(state.history), [state.history]);
  const indicators = useMemo(() => state.signal?.indicators ?? getLatestIndicators(state.history), [state.signal, state.history]);
  const backtest = useMemo(() => runEmaCrossoverBacktest(state.history), [state.history]);
  const alerts = useMemo(
    () =>
      buildAlerts({
        signal: state.signal,
        indicators,
        calendar: state.calendar,
        apiStatus: state.signal?.apiStatus,
        newsAnalysis: state.newsAnalysis,
        error: state.error,
      }),
    [state.signal, indicators, state.calendar, state.newsAnalysis, state.error],
  );

  const nextHighImpact = useMemo(
    () => (state.calendar ?? []).find((event) => ["very high", "high"].includes(event.impact)),
    [state.calendar],
  );

  return (
    <main className="min-h-screen px-4 py-5 md:px-6 lg:px-8">
      <div className="mx-auto max-w-[1480px]">
        <motion.header
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-5 overflow-hidden rounded-lg border border-white/10 bg-ink/90 p-5 shadow-panel md:p-7"
        >
          <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <h1 className="text-3xl font-semibold leading-tight text-white md:text-5xl">Gold Market Signal Pro</h1>
              <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-300 md:text-lg">
                Онлайн XAU/USD, макро-данные, ИИ-анализ новостей, технический анализ и риск-менеджмент.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <ApiStatusBadge label={state.loading ? "Данные загружаются" : "Помощник анализа"} tone="info" icon="activity" />
                <ApiStatusBadge label={DISCLAIMER.advisory} tone="warn" icon="shield" />
                <ApiStatusBadge label={DISCLAIMER.guarantee} tone="neutral" icon="shield" />
              </div>
            </div>
            <div className="flex max-w-xl flex-wrap gap-2 lg:justify-end">
              {buildStatusBadges(state).map((badge) => (
                <ApiStatusBadge key={badge.label} {...badge} />
              ))}
            </div>
          </div>
        </motion.header>

        <section className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <SignalCard label="Цена золота" value={formatUsd(state.price?.price)} detail="XAU/USD · USD за тройскую унцию" tone="warn" />
          <SignalCard label="Действие по сигналу" value={state.signal?.action ?? "Держать"} detail={DISCLAIMER.helper} tone={signalTone(state.signal?.score)} />
          <SignalCard label="Оценка сигнала" value={`${formatNumber(state.signal?.score, 0)}/100`} detail="Вес: техника, макро, новости и риск" />
          <SignalCard label="Уверенность" value={formatConfidence(state.signal?.confidence)} detail="Зависит от качества данных" tone={state.signal?.confidence === "high" ? "good" : state.signal?.confidence === "low" ? "bad" : "warn"} />
          <SignalCard label="Индекс USD" value={formatNumber(state.macro?.usdIndex, 2)} detail="Макро-драйвер" />
          <SignalCard label="Реальная доходность" value={formatPercent(state.macro?.realYield, 2)} detail="Альтернативные издержки" />
          <SignalCard
            label="Ближайшее важное событие"
            value={nextHighImpact?.name ?? "Нет данных"}
            detail={nextHighImpact ? formatZurichDateTime(nextHighImpact.time) : "Europe/Zurich"}
            tone={nextHighImpact?.impact === "very high" ? "bad" : "neutral"}
          />
        </section>

        <section className="grid gap-5 xl:grid-cols-[1.45fr_0.95fr]">
          <MainChart data={enrichedHistory} support={indicators.support} resistance={indicators.resistance} />
          <TechnicalPanel indicators={indicators} technicalScore={state.signal?.technicalScore ?? 0} />
        </section>

        <section className="mt-5 grid gap-5 xl:grid-cols-[1fr_1.05fr]">
          <MacroPanel macro={state.macro} macroScore={state.signal?.macroScore ?? 0} />
          <NewsPanel analysis={state.newsAnalysis} news={state.news} newsScore={state.signal?.newsScore ?? 0} />
        </section>

        <section className="mt-5 grid gap-5 xl:grid-cols-[0.95fr_1.05fr]">
          <EconomicCalendarPanel events={state.calendar} />
          <AlertsPanel alerts={alerts} />
        </section>

        <section className="mt-5 grid gap-5 xl:grid-cols-2">
          <RiskCalculator currentPrice={state.price?.price} />
          <PortfolioTracker currentPrice={state.price?.price} />
        </section>

        <section className="mt-5">
          <BacktestPanel result={backtest} />
        </section>

        <footer className="py-6 text-center text-sm leading-relaxed text-slate-500">
          {DISCLAIMER.advisory} · {DISCLAIMER.guarantee} · Сигналы являются осторожной рыночной аналитикой, а не призывом к действию.
        </footer>
      </div>
    </main>
  );
}

function signalTone(score) {
  if (score >= 75) return "good";
  if (score >= 60) return "info";
  if (score >= 45) return "neutral";
  if (score >= 30) return "warn";
  return "bad";
}

function formatConfidence(value) {
  if (value === "high") return "Высокая";
  if (value === "medium") return "Средняя";
  return "Низкая";
}

function buildStatusBadges(state) {
  const secrets = state.status?.secrets ?? {};
  const missingKey = Object.values(secrets).some((value) => value === false);
  const apiStatus = state.signal?.apiStatus ?? {};
  const badges = [];

  if (!state.loading && !apiStatus.fallbackActive && !apiStatus.apiError) {
    badges.push({ label: "Live API подключены", tone: "good", icon: "activity" });
  }
  if (apiStatus.fallbackActive || state.price?.isFallback || state.error) {
    badges.push({ label: "Fallback-данные активны", tone: "warn", icon: "alert" });
  }
  if (state.newsAnalysis?.source === "Gemini" && !state.newsAnalysis?.isFallback) {
    badges.push({ label: "Анализ Gemini активен", tone: "good", icon: "spark" });
  }
  if (state.newsAnalysis?.isFallback || apiStatus.geminiFallback) {
    badges.push({ label: "Keyword fallback активен", tone: "warn", icon: "spark" });
  }
  if (apiStatus.apiError || state.error) {
    badges.push({ label: "Ошибка API", tone: "bad", icon: "alert" });
  }
  if (state.status?.cacheBinding || Object.values(apiStatus.cache ?? {}).some(Boolean)) {
    badges.push({ label: "KV Cache активен", tone: "info", icon: "database" });
  }
  if (missingKey) {
    badges.push({ label: "API-ключ отсутствует", tone: "warn", icon: "shield" });
  }
  return badges.length ? badges : [{ label: "Fallback-данные активны", tone: "warn", icon: "alert" }];
}

function buildAlerts({ signal, indicators, calendar, apiStatus, newsAnalysis, error }) {
  const alerts = [];
  const score = Number(signal?.score);
  const rsi = Number(indicators?.rsi14);
  const price = Number(indicators?.price);
  const support = Number(indicators?.support);
  const resistance = Number(indicators?.resistance);

  if (score > 75) alerts.push("Сигнал > 75: проверить покупку, но без гарантии и без прямой торговой рекомендации.");
  if (Number.isFinite(rsi) && rsi > 75) alerts.push("RSI перекуплен: возможны откат или волатильность.");
  if (Number.isFinite(rsi) && rsi < 30) alerts.push("RSI перепродан: возможна обратная реакция, риск остается высоким.");
  if (Number.isFinite(price) && Number.isFinite(support) && support > 0 && ((price - support) / support) * 100 < 1.5) {
    alerts.push("Цена рядом с поддержкой: стоит наблюдать реакцию.");
  }
  if (Number.isFinite(price) && Number.isFinite(resistance) && resistance > 0 && ((resistance - price) / resistance) * 100 < 1.5) {
    alerts.push("Цена рядом с сопротивлением: возможны фиксация прибыли или отбой.");
  }
  if ((calendar ?? []).some((event) => eventIsToday(event) && ["very high", "high"].includes(event.impact))) {
    alerts.push("Сегодня важное макро-событие: возможна повышенная волатильность.");
  }
  if (apiStatus?.apiError || error) alerts.push("Ошибка API: dashboard использует fallback или кэш.");
  if (apiStatus?.geminiFallback || newsAnalysis?.isFallback) alerts.push("Активен keyword fallback вместо Gemini.");
  if (["bullish", "bearish"].includes(newsAnalysis?.overallSentiment) && Number(newsAnalysis?.score) > 75) {
    alerts.push(`Новостной сентимент сильно ${newsAnalysis.overallSentiment}: контекст стоит проверить осторожно.`);
  }
  if (apiStatus?.fallbackActive) alerts.push("Fallback-режим активен: качество данных ограничено.");
  if (Object.values(apiStatus?.cache ?? {}).includes("hit")) alerts.push("Использован KV Cache: данные могут немного запаздывать.");
  return alerts;
}

function eventIsToday(event) {
  const date = new Date(event?.time);
  if (Number.isNaN(date.getTime())) return false;
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Zurich",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(date) === formatter.format(new Date());
}
